import React, { useEffect, useState } from 'react';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Entity } from '../Entity/Entity';
import { EntityListLoading } from './EntityList.loading';
import { EntityListEmpty } from './EntityList.empty';
import { DeleteEntity } from '../DeleteEntity/DeleteEntity';
import { RenameEntity } from '../RenameEntity/RenameEntity';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { FullPageLoading } from '../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { useEntityList } from './useEntityList';
import { useUploadingFiles } from '../../state/selectors';
import { EntitiesDTO, EntityDTO, FOLDER_404_ERROR } from '../../DocumentVault.meta';
import { VideoModal } from '../VideoModal/VideoModal';
import { useNavigate } from '../../hooks/useNavigate';
import { useRouteParams } from '../../hooks/useRouteParams';
import { OrganizeModal } from '../OrganizeModal/OrganizeModal';
import { UseEntityListActions } from '../../hooks/useBaseEntityList';

export const EntityList = () => {
  const {
    isFetching,
    isDownloadingFile,
    currentVideoEntity,
    isError,
    error,
    refetch,
    data,
    actions: { onEntityPress, setCurrentVideoEntity },
  } = useEntityList();

  if (isFetching) {
    return <EntityListLoading />;
  }

  // If the folder does not exist, we want to route the user to the home folder
  // See the useListEntitiesQuery onError() method
  if (isError && error?.message !== FOLDER_404_ERROR) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={96}>
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <EntityListContent
      isDownloadingFile={isDownloadingFile}
      currentVideoEntity={currentVideoEntity}
      onEntityPress={onEntityPress}
      setCurrentVideoEntity={setCurrentVideoEntity}
      data={data}
    />
  );
};

interface EntityListContentProps extends UseEntityListActions {
  isDownloadingFile: boolean;
  currentVideoEntity: EntityDTO | null;
  data: EntitiesDTO;
}

const EntityListContent = ({
  isDownloadingFile,
  currentVideoEntity,
  onEntityPress,
  setCurrentVideoEntity,
  data,
}: EntityListContentProps) => {
  const [entityToDelete, setEntityToDelete] = useState<EntityDTO | null>(null);
  const [entityToRename, setEntityToRename] = useState<EntityDTO | null>(null);
  const [showOrganizeModal, setShowOrganizeModal] = useState<EntityDTO | null>(null);

  const uploadingFiles = useUploadingFiles();

  const navigate = useNavigate();

  const { filterableByYear: routeFilterableByYear } = useRouteParams();

  const { items, filterableByYear } = data;

  useEffect(() => {
    if (filterableByYear !== routeFilterableByYear) {
      navigate({
        filterableByYear,
      });
    }
  }, [routeFilterableByYear, filterableByYear, navigate]);

  if (items.length === 0 && Object.keys(uploadingFiles).length === 0) {
    return <EntityListEmpty />;
  }

  return (
    <>
      {isDownloadingFile && <FullPageLoading />}

      <>
        {items.map((entity, index) => {
          return (
            <Entity
              key={index} // eslint-disable-line react/no-array-index-key
              entity={entity}
              onEntityClick={() => onEntityPress(entity)}
              onEntityDelete={() => setEntityToDelete(entity)}
              onRenameClick={() => setEntityToRename(entity)}
              borderBottom={index === items.length - 1 ? 'none' : '1px solid'}
              onOrganizeClick={() => setShowOrganizeModal(entity)}
            />
          );
        })}
      </>

      {entityToDelete && (
        <DeleteEntity entity={entityToDelete} onRequestClose={() => setEntityToDelete(null)} />
      )}

      {entityToRename && (
        <RenameEntity entity={entityToRename} onClose={() => setEntityToRename(null)} />
      )}

      {showOrganizeModal && (
        <OrganizeModal
          selectedEntity={showOrganizeModal}
          onClose={() => setShowOrganizeModal(null)}
        />
      )}

      <VideoModal entity={currentVideoEntity} onClose={() => setCurrentVideoEntity(null)} />
    </>
  );
};
